// Per-lead Marketo program routing — the pure helpers behind Delivery Setup's
// "Route to a different program by value" editor (2026-10-14, built for Uber
// Restaurants: asset title -> Marketo program). Backend half:
// app/utils/delivery/program_routes.py, stored as
// target_config.program_routes = {field, routes: [{title, aliases, program_name,
// program_id?, program_status?}]}.
//
// Editor rows are all strings so inputs stay controlled:
//   { title, aliases ("a, b"), programId, programName, programStatus }

// No default: the field to route on is client-specific (Uber's is cSContentTitle).
// An earlier version prefilled it, which showed Uber's value on every Marketo destination.
export const DEFAULT_ROUTE_FIELD = '';

const HEADER_WORDS = new Set(['asset title', 'asset landing page', 'program id', 'title', 'landing page', 'url']);

// Rows from a pasted client table. Accepts a spreadsheet paste (one row per
// line, tab-separated: title, landing page URL, program id) or the same table
// flattened to one value per line (title / URL / id), as an email shows it.
// Header words and URLs are ignored; a row with no id is kept (the PM can type
// it) rather than dropped silently.
export function parseRoutePaste(text) {
  const out = [];
  let pending = null;
  const flush = () => {
    if (pending && pending.title) out.push({ title: pending.title, programId: pending.programId || '' });
    pending = null;
  };
  String(text || '').split(/\r?\n/).forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    if (line.indexOf('\t') !== -1) {
      flush();
      const cells = line.split('\t').map((c) => c.trim()).filter((c) => c !== '');
      if (!cells.length || HEADER_WORDS.has(cells[0].toLowerCase())) return;
      const idCell = cells.slice(1).reverse().find((c) => /^\d+$/.test(c));
      out.push({ title: cells[0], programId: idCell || '' });
      return;
    }
    if (HEADER_WORDS.has(line.toLowerCase())) return;
    if (/^https?:\/\//i.test(line)) return;
    if (/^\d+$/.test(line)) {
      if (pending) pending.programId = line;
      return;
    }
    flush();
    pending = { title: line, programId: '' };
  });
  flush();
  return out;
}

export function emptyRouteRow() {
  return { title: '', aliases: '', programId: '', programName: '', programStatus: '' };
}

// Saved config -> editor state.
export function routesFromConfig(cfg) {
  const routes = (cfg && Array.isArray(cfg.routes)) ? cfg.routes : [];
  return {
    field: (cfg && cfg.field) || DEFAULT_ROUTE_FIELD,
    rows: routes.map((r) => ({
      title: r.title || '',
      aliases: (r.aliases || []).join(', '),
      programId: r.program_id == null ? '' : String(r.program_id),
      programName: r.program_name || '',
      programStatus: r.program_status || '',
    })),
  };
}

function isBlankRow(row) {
  return !row.title.trim() && !row.aliases.trim() && !row.programId.trim() && !row.programName.trim();
}

// Why the table can't be saved yet (empty = fine). Completely blank rows are
// ignored, not errors.
export function routeProblems(field, rows) {
  const used = rows.filter((r) => !isBlankRow(r));
  if (!used.length) return [];
  const problems = [];
  if (!String(field || '').trim()) problems.push('Choose the destination field to route on.');
  used.forEach((r, i) => {
    const label = r.title.trim() ? `"${r.title.trim()}"` : `Route ${i + 1}`;
    if (!r.title.trim()) problems.push(`${label} has no asset title.`);
    if (!r.programName.trim()) problems.push(`${label} has no program name — enter the program ID and click Look up.`);
  });
  return problems;
}

// Editor state -> target_config.program_routes (null when there are no routes,
// which clears routing and falls back to the destination's single Program Name).
export function routesToConfig(field, rows) {
  const used = rows.filter((r) => !isBlankRow(r));
  if (!used.length) return null;
  return {
    field: String(field || '').trim(),
    routes: used.map((r) => {
      const route = {
        title: r.title.trim(),
        aliases: r.aliases.split(',').map((a) => a.trim()).filter((a) => a),
        program_name: r.programName.trim(),
      };
      if (/^\d+$/.test(r.programId.trim())) route.program_id = Number(r.programId.trim());
      if (r.programStatus && r.programStatus.trim()) route.program_status = r.programStatus.trim();
      return route;
    }),
  };
}

// Delivered/simulated leads per Marketo program, from a job's results rows
// (each carries program_name when routing applied): [{program, count}], most
// first. Empty when no row names a program.
export function programBreakdown(results) {
  const counts = {};
  (results || []).forEach((r) => {
    if (r.program_name && (r.status === 'Delivered' || r.status === 'Simulated')) {
      counts[r.program_name] = (counts[r.program_name] || 0) + 1;
    }
  });
  return Object.keys(counts)
    .map((program) => ({ program, count: counts[program] }))
    .sort((a, b) => b.count - a.count || a.program.localeCompare(b.program));
}
